import { supabase } from '@/lib/supabase';

/**
 * 오디오 파일을 Supabase Storage에 업로드
 * @param audioBlob 업로드할 오디오 Blob
 * @param messageId 메시지 ID (파일명 생성에 사용)
 * @returns 저장된 파일의 경로 또는 에러시 null
 */
async function uploadAudioToSupabase(
  audioBlob: Blob,
  messageId: string
): Promise<string | null> {
  try {
    const filePath = `tts/${messageId}.mp3`;
    
    console.log(`오디오 파일 업로드 시작: ${filePath}`);
    
    const { data, error } = await supabase.storage
      .from('audio')
      .upload(filePath, audioBlob, {
        contentType: 'audio/mpeg',
        upsert: true // 같은 경로에 파일이 있으면 덮어쓰기
      });

    if (error) {
      console.error('오디오 업로드 오류:', error);
      return null;
    }

    console.log('오디오 업로드 성공:', data?.path);
    
    // 파일 공개 URL 가져오기
    const { data: publicUrlData } = supabase.storage
      .from('audio')
      .getPublicUrl(filePath);
    
    return publicUrlData.publicUrl;
  } catch (_error: unknown) {
    console.error('오디오 업로드 중 예외 발생:', _error);
    return null;
  }
}

/**
 * 메시지 ID로 오디오 파일 URL 저장
 * @param messageId 메시지 ID
 * @param audioUrl 오디오 파일 URL
 */
async function saveAudioUrlToMessage(
  messageId: string, 
  audioUrl: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('chat_messages')
      .update({ voice_file: audioUrl })
      .eq('message_id', messageId);
    
    if (error) {
      console.error('오디오 URL 저장 오류:', error);
      return false;
    }
    
    console.log(`메시지 ID ${messageId}에 오디오 URL 저장 성공`);
    return true;
  } catch (_error: unknown) {
    console.error('오디오 URL 저장 중 예외 발생:', _error);
    return false;
  }
}

/**
 * 메시지 ID로 저장된 오디오 URL 조회
 * @param messageId 메시지 ID
 * @returns 저장된 오디오 URL 또는 null
 */
export async function getAudioUrlForMessage(messageId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('voice_file')
      .eq('message_id', messageId)
      .maybeSingle();
    
    if (error) {
      console.error('오디오 URL 조회 오류:', error);
      return null;
    }
    
    return data?.voice_file || null;
  } catch (_error: unknown) {
    console.error('오디오 URL 조회 중 예외 발생:', _error);
    return null;
  }
}

// 현재 재생 중인 AudioElement 추적
let currentAudioElement: HTMLAudioElement | null = null;

/**
 * Blob을 AudioBuffer로 변환하여 재생
 * @param audioBlob 오디오 Blob
 * @returns 성공 여부
 */
async function playAudioFromBlob(audioBlob: Blob): Promise<boolean> {
  try {
    const audioUrl = URL.createObjectURL(audioBlob);
    // 이전 재생 중인 오디오 중지
    if (currentAudioElement) {
      currentAudioElement.pause();
      currentAudioElement.removeAttribute('src');
      try { currentAudioElement.load(); } catch (_error: unknown) {
        console.warn('오디오 로드 실패:', _error);
      }
      currentAudioElement = null;
    }
    const audio = new Audio(audioUrl);
    // 현재 AudioElement 저장
    currentAudioElement = audio;
    
    return new Promise((resolve) => {
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        resolve(true);
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        console.error('오디오 재생 오류');
        resolve(false);
      };
      
      audio.play().catch((error: unknown) => {
        console.error('오디오 재생 오류:', error);
        URL.revokeObjectURL(audioUrl);
        resolve(false);
      });
    });
  } catch (_error: unknown) {
    console.error('오디오 재생 중 예외 발생:', _error);
    return false;
  }
}

/**
 * URL에서 오디오 파일을 가져와서 재생
 * @param audioUrl 오디오 파일 URL
 * @returns 성공 여부
 */
export async function playAudioFromUrl(audioUrl: string): Promise<boolean> {
  try {
    const response = await fetch(audioUrl);
    if (!response.ok) {
      console.error(`오디오 파일 가져오기 오류: ${response.status}`);
      return false;
    }
    
    const audioBlob = await response.blob();
    return await playAudioFromBlob(audioBlob);
  } catch (_error: unknown) {
    console.error('URL에서 오디오 재생 중 예외 발생:', _error);
    return false;
  }
}

async function playAudioFromUrlFallback(audioUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio: HTMLAudioElement = new Audio(audioUrl);
    audio.play().then(resolve).catch((error: unknown): void => {
      console.error('오디오 자동재생이 차단되었습니다:', error);
      // 자동재생이 차단된 경우, 사용자 상호작용 후 재시도
      const resumePlayback = (): void => {
        audio.play().then(resolve).catch(reject);
        window.removeEventListener('click', resumePlayback);
        window.removeEventListener('touchstart', resumePlayback);
        window.removeEventListener('keydown', resumePlayback);
      };
      window.addEventListener('click', resumePlayback);
      window.addEventListener('touchstart', resumePlayback);
      window.addEventListener('keydown', resumePlayback);
    });
  });
}

/**
 * 현재 재생 중인 오디오를 중단합니다.
 */
export function stopAudioPlayback(): void {
  if (currentAudioElement) {
    currentAudioElement.pause();
    currentAudioElement.currentTime = 0;
    currentAudioElement.removeAttribute('src');
    try { currentAudioElement.load(); } catch (_error: unknown) {
      console.warn('오디오 로드 실패:', _error);
    }
    currentAudioElement = null;
  }
}

// 현재 진행 중인 오디오 생성 요청들을 추적
const pendingAudioGenerations = new Map<string, Promise<string | null>>();

/**
 * ElevenLabs 스트림 응답을 캡처하고 Supabase에 저장하는 함수
 */
export async function generateAndSaveAudio(
  text: string,
  messageId: string,
  voiceId: string = "j9qX8lQT2vsTocP0jI9L",
  onComplete: () => void,
  onError: (error: unknown) => void
): Promise<void> {
  const elevenlabsApiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
  if (!elevenlabsApiKey) {
    onError(new Error('ElevenLabs API 키가 설정되지 않았습니다.'));
    return;
  }

  // 이미 진행 중인 생성 요청이 있는지 확인
  if (pendingAudioGenerations.has(messageId)) {
    console.log(`메시지 ID ${messageId}에 대한 오디오 생성이 이미 진행 중입니다.`);
    try {
      const audioUrl = await pendingAudioGenerations.get(messageId);
      if (audioUrl) {
        const success = await playAudioFromUrl(audioUrl);
        if (success) {
          onComplete();
        } else {
          onError(new Error('이미 생성 중인 오디오 재생 실패'));
        }
      } else {
        onError(new Error('이미 생성 중인 오디오 URL이 null입니다'));
      }
    } catch (_error: unknown) {
      onError(_error);
    }
    return;
  }

  // 새 생성 작업 시작 및 추적
  const audioGenerationPromise = (async (): Promise<string | null> => {
    try {
      // 1. 먼저 기존 음성파일 URL이 있는지 확인
      const existingAudioUrl = await getAudioUrlForMessage(messageId);
      
      if (existingAudioUrl) {
        console.log(`메시지 ID ${messageId}의 기존 오디오 파일 발견:`, existingAudioUrl);
        
        // 기존 파일 재생
        const success = await playAudioFromUrl(existingAudioUrl);
        if (success) {
          // 재생 완료 처리
          return existingAudioUrl;
        } else {
          console.log('저장된 음성파일 재생 실패, 새로 생성합니다.');
          // 재생 실패 시 계속해서 새로 생성
        }
      }
      
      // 2. 텍스트 전처리
      const sanitizedText: string = text.replace(/[ㅎㅋ]/g, "").replace(/\?/g, "???").replace(/!/g, "!!!");
      
      // 2. ElevenLabs API에 요청하여 음성 생성
      const apiUrl = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}/stream?output_format=mp3_44100_128`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': elevenlabsApiKey
        },
        body: JSON.stringify({
          text: sanitizedText,
          model_id: "eleven_flash_v2_5", // 빠른 응답을 위한 Flash 모델
          voice_settings: {
            stability: 0.9,
            similarity_boost: 0.9
          },
          language_code: "ko"
        })
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API 오류: ${response.status} ${response.statusText}`);
      }

      // 3. 응답을 Blob으로 변환
      const audioBlob = await response.blob();
      
      // 4. Supabase에 업로드
      const audioUrl = await uploadAudioToSupabase(audioBlob, messageId);
      
      if (audioUrl) {
        // 5. 메시지에 오디오 URL 저장
        await saveAudioUrlToMessage(messageId, audioUrl);
        return audioUrl;
      } 
      return null;
    } catch (_error: unknown) {
      console.error('음성 생성 및 저장 중 오류:', _error);
      throw _error;
    } finally {
      // 요청 추적에서 제거
      pendingAudioGenerations.delete(messageId);
    }
  })();

  // 요청 추적에 추가
  pendingAudioGenerations.set(messageId, audioGenerationPromise);

  try {
    // 생성 및 업로드 완료 대기
    const audioUrl = await audioGenerationPromise;
    
    if (audioUrl) {
      // 업로드된 오디오 재생
      await playAudioFromUrlFallback(audioUrl);
      onComplete();
    } else {
      onError(new Error('오디오 URL이 null입니다'));
    }
  } catch (_error: unknown) {
    console.error('음성 생성 Promise 처리 중 오류:', _error);
    onError(_error);
  }
}